/** Command rows, outcomes and the request body (V07/V08; UI spec §4). */
import {
  ContractViolation,
  bool,
  exact,
  field,
  instant,
  intMap,
  listOf,
  nullable,
  oneOf,
  record,
  str,
} from "./decode.ts";
import { ENVIRONMENTS, MODES, type Environment, type Mode } from "./envelope.ts";

export const COMMAND_STATES = [
  "RECEIVED",
  "PENDING_APPROVAL",
  "ACCEPTED",
  "REJECTED",
  "APPLYING",
  "APPLIED",
  "FAILED",
  "UNKNOWN",
  "EXPIRED",
  "CANCELLED",
] as const;
export const ORDER_STATES = [
  "INTENDED",
  "RISK_RESERVED",
  "SUBMIT_PENDING",
  "UNKNOWN",
  "ACKNOWLEDGED",
  "WORKING",
  "PARTIALLY_FILLED",
  "CANCEL_PENDING",
  "REPLACE_PENDING",
  "FILLED",
  "CANCELLED",
  "REJECTED",
] as const;
/**
 * Every `ssi.contracts.enums.CommandAction` value, in the enum's order (members are only ever
 * appended, ADR-0002). An out-of-band record names the action its code is for (build-log item 168).
 */
export const COMMAND_ACTIONS = [
  "PAUSE_ENTRIES",
  "REDUCE_POSITIONS",
  "FLATTEN",
  "CANCEL_ORDER",
  "CANCEL_ALL",
  "RESUME_ENTRIES",
  "CHANGE_RISK",
  "ACTIVATE_STRATEGY",
  "ENABLE_LIVE",
  "CONNECT_BROKER",
  "MANAGE_ACCESS",
  "OPERATE_RELEASES",
  "ACKNOWLEDGE_INCIDENT",
  "REQUEST_RECONCILIATION",
  "TAKEOVER",
  "MODIFY_ORDER",
] as const;
export type CommandAction = (typeof COMMAND_ACTIONS)[number];
export const RETRY_GUIDANCE = ["NONE", "REQUERY_SAME_IDENTITY", "RECONCILIATION_REQUIRED"] as const;

export type CommandState = (typeof COMMAND_STATES)[number];
export type OrderState = (typeof ORDER_STATES)[number];

/** A row of the `commands` projection: a command's safe view. */
export interface CommandRow {
  readonly commandId: string;
  readonly actorIdentityId: string;
  readonly accountId: string;
  readonly environment: Environment;
  readonly action: string;
  readonly state: CommandState;
  readonly reason: string;
  readonly payloadDigest: string;
  readonly targetVersions: ReadonlyMap<string, number>;
  readonly receivedAt: string;
  readonly decidedAt: string | null;
  readonly appliedAt: string | null;
  readonly expiresAt: string;
  readonly approvalId: string | null;
}

/** Decode a `commands` projection row. */
export function decodeCommandRow(value: unknown, path = "command"): CommandRow {
  const found = exact(value, path, [
    "command_id",
    "actor_identity_id",
    "account_id",
    "environment",
    "action",
    "state",
    "reason",
    "payload_digest",
    "target_versions",
    "received_at",
    "decided_at",
    "applied_at",
    "expires_at",
    "approval_id",
  ]);
  return {
    commandId: field(found, path, "command_id", str),
    actorIdentityId: field(found, path, "actor_identity_id", str),
    accountId: field(found, path, "account_id", str),
    environment: field(found, path, "environment", oneOf(ENVIRONMENTS)),
    action: field(found, path, "action", str),
    state: field(found, path, "state", oneOf(COMMAND_STATES)),
    reason: field(found, path, "reason", str),
    payloadDigest: field(found, path, "payload_digest", str),
    targetVersions: field(found, path, "target_versions", intMap),
    receivedAt: field(found, path, "received_at", instant),
    decidedAt: field(found, path, "decided_at", nullable(instant)),
    appliedAt: field(found, path, "applied_at", nullable(instant)),
    expiresAt: field(found, path, "expires_at", instant),
    approvalId: field(found, path, "approval_id", nullable(str)),
  };
}

/** One application effect: a journaled record the controller wrote. */
export interface EffectRef {
  readonly kind: "ACCOUNT_TRANSITION" | "EXECUTION_REPORT";
  readonly eventId: string;
}

/** One order's broker-reported state (never a locally derived fill). */
export interface BrokerState {
  readonly orderId: string;
  readonly brokerState: OrderState;
}

/** A command's outcome as the API displays it: the three displays, never a fill. */
export interface CommandOutcome {
  readonly commandId: string;
  readonly state: CommandState;
  readonly receivedAt: string;
  readonly decidedAt: string | null;
  readonly appliedAt: string | null;
  readonly applicationEffect: readonly EffectRef[];
  readonly brokerStates: readonly BrokerState[];
  readonly unknown: boolean;
  readonly retryGuidance: (typeof RETRY_GUIDANCE)[number];
}

const OUTCOME_KEYS = [
  "command_id",
  "state",
  "received_at",
  "decided_at",
  "applied_at",
  "application_effect",
  "broker_states",
  "unknown",
  "retry_guidance",
] as const;

function readEffect(value: unknown, path: string): EffectRef {
  const found = exact(value, path, ["kind", "event_id"]);
  return {
    kind: field(found, path, "kind", oneOf(["ACCOUNT_TRANSITION", "EXECUTION_REPORT"] as const)),
    eventId: field(found, path, "event_id", str),
  };
}

function readBroker(value: unknown, path: string): BrokerState {
  const found = exact(value, path, ["order_id", "broker_state"]);
  return {
    orderId: field(found, path, "order_id", str),
    brokerState: field(found, path, "broker_state", oneOf(ORDER_STATES)),
  };
}

function readOutcome(found: Record<string, unknown>, path: string): CommandOutcome {
  return {
    commandId: field(found, path, "command_id", str),
    state: field(found, path, "state", oneOf(COMMAND_STATES)),
    receivedAt: field(found, path, "received_at", instant),
    decidedAt: field(found, path, "decided_at", nullable(instant)),
    appliedAt: field(found, path, "applied_at", nullable(instant)),
    applicationEffect: field(found, path, "application_effect", listOf(readEffect)),
    brokerStates: field(found, path, "broker_states", listOf(readBroker)),
    unknown: field(found, path, "unknown", bool),
    retryGuidance: field(found, path, "retry_guidance", oneOf(RETRY_GUIDANCE)),
  };
}

/** Decode `GET /api/v1/commands/{id}`: `unknown` holds exactly for APPLYING and UNKNOWN. */
export function decodeCommandOutcome(value: unknown): CommandOutcome {
  const path = "outcome";
  const outcome = readOutcome(exact(value, path, OUTCOME_KEYS), path);
  if (outcome.unknown !== (outcome.state === "APPLYING" || outcome.state === "UNKNOWN")) {
    throw new ContractViolation(path, "unknown exactly for APPLYING and UNKNOWN");
  }
  return outcome;
}

/** The answer to `POST /api/v1/commands`: the outcome plus the approval and the repeat flag. */
export interface Admitted extends CommandOutcome {
  readonly approvalId: string | null;
  readonly replayed: boolean;
  readonly reasons: readonly string[];
}

/** Decode the admission answer. */
export function decodeAdmitted(value: unknown): Admitted {
  const path = "admitted";
  const found = exact(value, path, [...OUTCOME_KEYS, "approval_id", "replayed", "reasons"]);
  return {
    ...readOutcome(found, path),
    approvalId: field(found, path, "approval_id", nullable(str)),
    replayed: field(found, path, "replayed", bool),
    reasons: field(found, path, "reasons", listOf(str)),
  };
}

/** A payload value: a scalar, or the written review a resume carries (`{"text": ...}`, INC-0807). */
export type PayloadValue = string | number | boolean | null | { readonly text: string };

/** The command body the browser submits (identity and rights are never in it). */
export interface CommandRequest {
  readonly clientKey: string;
  readonly action: string;
  readonly accountId: string;
  readonly environment: Environment;
  readonly mode: Mode;
  readonly payload: Readonly<Record<string, PayloadValue>>;
  readonly reason: string;
  readonly targetVersions: Readonly<Record<string, number>>;
  readonly expiresAt: string;
}

/** Decode a request body (fixture round trip). */
export function decodeCommandRequest(value: unknown): CommandRequest {
  const path = "request";
  const found = exact(value, path, [
    "client_key",
    "action",
    "account_id",
    "environment",
    "mode",
    "payload",
    "reason",
    "target_versions",
    "expires_at",
  ]);
  return {
    clientKey: field(found, path, "client_key", str),
    action: field(found, path, "action", str),
    accountId: field(found, path, "account_id", str),
    environment: field(found, path, "environment", oneOf(ENVIRONMENTS)),
    mode: field(found, path, "mode", oneOf(MODES)),
    payload: field(found, path, "payload", record) as CommandRequest["payload"],
    reason: field(found, path, "reason", str),
    targetVersions: Object.fromEntries(field(found, path, "target_versions", intMap)),
    expiresAt: field(found, path, "expires_at", instant),
  };
}

/** Encode a request as the route's JSON keys. */
export function encodeCommandRequest(request: CommandRequest): Record<string, unknown> {
  return {
    client_key: request.clientKey,
    action: request.action,
    account_id: request.accountId,
    environment: request.environment,
    mode: request.mode,
    payload: request.payload,
    reason: request.reason,
    target_versions: request.targetVersions,
    expires_at: request.expiresAt,
  };
}
